import http from '../../../../api/http-common.jsx';

class CatalogService {
  constructor() {
    this.catalogs = new Map();
    this.catalogsPromise = null;
    this.isLoaded = false;
    // uuid con el que se cargó this.catalogsPromise; si cambia (otra ficha, posiblemente
    // de otro cultivo) hay que recargar en vez de reusar el caché de la ficha anterior
    this.loadedForUuid = null;
  }

  /**
   * Carga los catálogos desde la API, filtrados por el id_cultivo de la ficha (uuid) si se indica.
   * @param {string} [uuid] - uuid de la ficha que se está editando (sic_informacion_generales.uuid)
   * @returns {Promise}
   */
  async loadCatalogs(uuid = null) {
    if (this.catalogsPromise && this.loadedForUuid === uuid) {
      return this.catalogsPromise;
    }

    this.loadedForUuid = uuid;
    this.catalogsPromise = this._fetchCatalogs(uuid);
    return this.catalogsPromise;
  }

  async _fetchCatalogs(uuid) {
    try {
      const query = uuid ? `?uuid=${encodeURIComponent(uuid)}` : '';
      const response = await http.get(`/ficha/formulario/master/catalogos${query}`);
      const catalogData = response.data.data;

      // Limpiar catálogos existentes
      this.catalogs.clear();

      // Agrupar los elementos del catálogo por lista
      catalogData.forEach(item => {
        if (!this.catalogs.has(item.lista)) {
          this.catalogs.set(item.lista, []);
        }
        
        // Solo agregar elementos que no estén marcados como eliminados
        if (item.deleted_at === 'NULL' || item.deleted_at === null) {
          this.catalogs.get(item.lista).push({
            id: item.id_odk,
            value: item.id_odk,
            originalId: item.id, // Agregar el ID original para comparaciones
            label: item.nombre,
            municipio: item.municipio,
            zona: item.zona,
            color: item.color
          });
        }
      });

      // Ordenar las opciones por id_odk para cada lista
      this.catalogs.forEach((options, listName) => {
        options.sort((a, b) => {
          // Manejar casos especiales como "name_key", "98", "99"
          if (a.id === 'name_key') return -1;
          if (b.id === 'name_key') return 1;
          if (a.id === '99') return 1;
          if (b.id === '99') return -1;
          if (a.id === '98') return 1;
          if (b.id === '98') return -1;
          
          // Ordenar numéricamente si ambos son números
          const aNum = parseInt(a.id);
          const bNum = parseInt(b.id);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
          
          // Ordenar alfabéticamente si no son números
          return a.id.localeCompare(b.id);
        });
      });

      this.isLoaded = true;
      return this.catalogs;
    } catch (error) {
      // console.error('Error al cargar catálogos desde API:', error);
      this.catalogsPromise = null;
      this.loadedForUuid = null;
      throw error;
    }
  }

  /**
   * Obtiene las opciones de una lista específica
   * @param {string} listName - Nombre de la lista del catálogo
   * @returns {Promise<Array>} Array de opciones con formato {id, value, label}
   */
  async getOptions(listName) {
    if (!listName) {
      return [];
    }

    // Asegurar que los catálogos estén cargados (reutiliza el uuid con el que ya se cargaron, si lo hubo)
    await this.loadCatalogs(this.loadedForUuid);

    const options = this.catalogs.get(listName);
    if (!options) {
      return [];
    }

    return options;
  }

  /**
   * Obtiene estadísticas del catálogo
   * @returns {Promise<Object>}
   */
  async getStatistics() {
    await this.loadCatalogs(this.loadedForUuid);
    const totalLists = this.catalogs.size;
    let totalOptions = 0;
    
    this.catalogs.forEach(options => {
      totalOptions += options.length;
    });

    return {
      totalLists,
      totalOptions,
      averageOptionsPerList: Math.round(totalOptions / totalLists)
    };
  }

}

// Crear una instancia singleton
const catalogService = new CatalogService();

export default catalogService;